import { useState, useEffect } from 'react';

/**
 * Hook to request and track user geolocation.
 * Can be used site-wide for analytics or live tracking.
 */
const useGeolocationTracker = () => {
    const [location, setLocation] = useState(null);
    const [error, setError] = useState(null);
    const [permissionStatus, setPermissionStatus] = useState('prompt');

    useEffect(() => {
        // Check initial permission status if supported
        if (navigator.permissions && navigator.permissions.query) {
            navigator.permissions.query({ name: 'geolocation' }).then((result) => {
                setPermissionStatus(result.state);
                result.onchange = () => {
                    setPermissionStatus(result.state);
                };
            });
        }

        let watcherId = null;

        const startTracking = () => {
            if (!("geolocation" in navigator)) {
                setError("Geolocation not supported");
                return;
            }

            watcherId = navigator.geolocation.watchPosition(
                (position) => {
                    const { latitude, longitude, accuracy, heading } = position.coords;
                    const coords = { lat: latitude, lng: longitude, accuracy, heading };
                    setLocation(coords);

                    localStorage.setItem('user_last_location', JSON.stringify({
                        ...coords,
                        timestamp: Date.now()
                    }));
                },
                (err) => {
                    // If high accuracy fails on desktop/macOS, silently retry with low accuracy
                    if (err.code === 2 || err.code === 3) {
                        navigator.geolocation.getCurrentPosition(
                            (p) => {
                                const coords = { lat: p.coords.latitude, lng: p.coords.longitude };
                                setLocation(coords);
                            },
                            () => { /* silent fallback */ },
                            { enableHighAccuracy: false, timeout: 5000 }
                        );
                    } else {
                        setError(err.message);
                    }
                },
                {
                    enableHighAccuracy: false,
                    maximumAge: 30000,
                    timeout: 10000
                }
            );
        };

        // If permission is already granted, start tracking immediately
        if (permissionStatus === 'granted') {
            startTracking();
        }

        // Cleanup
        return () => {
            if (watcherId !== null) {
                navigator.geolocation.clearWatch(watcherId);
            }
        };
    }, [permissionStatus]);

    const requestPermission = () => {
        return new Promise((resolve, reject) => {
            if (!("geolocation" in navigator)) {
                reject("Geolocation not supported");
                return;
            }

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    const coords = { lat: latitude, lng: longitude };
                    setLocation(coords);
                    setPermissionStatus('granted');
                    resolve(coords);
                },
                (err) => {
                    // Try low accuracy fallback silently before rejecting
                    navigator.geolocation.getCurrentPosition(
                        (p) => {
                            const coords = { lat: p.coords.latitude, lng: p.coords.longitude };
                            setLocation(coords);
                            setPermissionStatus('granted');
                            resolve(coords);
                        },
                        () => {
                            setError(err.message);
                            reject(err);
                        },
                        { enableHighAccuracy: false, timeout: 5000 }
                    );
                },
                { enableHighAccuracy: false, timeout: 5000 }
            );
        });
    };

    return { location, error, permissionStatus, requestPermission };
};

export default useGeolocationTracker;
